import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { motion } from "framer-motion";
import { Download as DownloadIcon } from "@mui/icons-material";
import XLSX from "xlsx-js-style";
import { saveAs } from "file-saver";
import {
  Box,
  Container,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Button,
  Chip,
  CircularProgress,
  Typography,
  IconButton,
  InputAdornment,
  Stack,
  useTheme,
  Dialog,
  Switch,
  TablePagination,
} from "@mui/material";
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Search as SearchIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  Event as EventIcon,
  Style as StyleIcon,
  Close as CloseIcon,
} from "@mui/icons-material";
import {
  fetchLeaveTypes,
  deleteExistingLeaveType,
  toggleLeaveTypeStatus,
} from "store/features/company/leaveManagement/leaveType/leaveTypeActions";
import {
  selectLeaveTypes,
  selectLeaveTypeLoading,
} from "store/features/company/leaveManagement/leaveType/leaveTypeSelectors";
import AddLeaveTypeModal from "./AddLeaveTypeModal";
import EditLeaveTypeModal from "./EditLeaveTypeModal";
import leaveTypesSvg from "assets/folders/leave-types.svg";
import { MdEventBusy } from "react-icons/md";
import usePermissions from "hooks/usePermissions";

const LeaveType = () => {
  const theme = useTheme();
  const dispatch = useDispatch();
  const leaveTypes = useSelector(selectLeaveTypes);
  const loading = useSelector(selectLeaveTypeLoading);

  const [togglingStatus, setTogglingStatus] = useState(false);

  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [leaveTypeToDelete, setLeaveTypeToDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedLeaveType, setSelectedLeaveType] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");

  // Pagination State
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const { hasPermission, hasAnyPermission } = usePermissions();

  const handleStatusToggle = async (leaveType) => {
    setTogglingStatus(true);
    const result = await dispatch(
      toggleLeaveTypeStatus(leaveType.id, leaveType.status),
    );
    if (result.success) {
    }
    setTogglingStatus(false);
  };

  useEffect(() => {
    dispatch(fetchLeaveTypes());
  }, [dispatch]);

  const filteredLeaveTypes = leaveTypes.filter((leaveType) => {
    const matchesSearch =
      searchTerm === "" ||
      leaveType.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      leaveType.code?.toLowerCase().includes(searchTerm.toLowerCase());

    const matchesStatus =
      statusFilter === "all" || leaveType.status === statusFilter;

    return matchesSearch && matchesStatus;
  });

  const formatDate = (dateString) => {
    if (!dateString) return "-";
    const date = new Date(dateString);
    return date.toLocaleDateString("en-IN", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  };

  const formatDateTime = (dateString) => {
    if (!dateString) return "-";
    const date = new Date(dateString);
    return date.toLocaleDateString("en-IN", {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const handleDeleteClick = (leaveType) => {
    setLeaveTypeToDelete(leaveType);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (leaveTypeToDelete) {
      setDeleting(true);
      const result = await dispatch(
        deleteExistingLeaveType(leaveTypeToDelete.id),
      );
      if (result.success) {
        dispatch(fetchLeaveTypes());
        setDeleteDialogOpen(false);
        setLeaveTypeToDelete(null);
      }
      setDeleting(false);
    }
  };

  const handleDeleteCancel = () => {
    setDeleteDialogOpen(false);
    setLeaveTypeToDelete(null);
  };

  if (!hasPermission("leave_type.view")) {
    return null;
  }
  const exportLeaveTypesToExcel = () => {
    if (!filteredLeaveTypes || filteredLeaveTypes.length === 0) {
      alert("No data to export");
      return;
    }

    const wb = XLSX.utils.book_new();
    const ws = XLSX.utils.aoa_to_sheet([]);

    // Yellow header style
    const yellowHeaderStyle = {
      font: {
        bold: true,
        sz: 12,
        color: { rgb: "000000" },
      },
      fill: {
        fgColor: { rgb: "FFFF00" },
      },
      alignment: {
        horizontal: "center",
        vertical: "center",
      },
      border: {
        top: { style: "thin", color: { rgb: "000000" } },
        bottom: { style: "thin", color: { rgb: "000000" } },
        left: { style: "thin", color: { rgb: "000000" } },
        right: { style: "thin", color: { rgb: "000000" } },
      },
    };

    let row = 0;

    // Title
    XLSX.utils.sheet_add_aoa(ws, [["LEAVE TYPES REPORT"]], {
      origin: `A${row + 1}`,
    });
    ws["!merges"] = [{ s: { r: row, c: 0 }, e: { r: row, c: 8 } }];
    ws[`A${row + 1}`].s = {
      font: { bold: true, sz: 14, color: { rgb: "000000" } },
      fill: { fgColor: { rgb: "FFFF00" } },
      alignment: { horizontal: "center", vertical: "center" },
    };
    row += 2;

    // Summary
    XLSX.utils.sheet_add_aoa(ws, [["SUMMARY"]], { origin: `A${row + 1}` });
    ws["!merges"].push({ s: { r: row, c: 0 }, e: { r: row, c: 1 } });
    ws[`A${row + 1}`].s = yellowHeaderStyle;
    row++;

    XLSX.utils.sheet_add_aoa(
      ws,
      [
        ["Metric", "Value"],
        ["Total Leave Types", filteredLeaveTypes.length],
        [
          "Active",
          filteredLeaveTypes.filter((l) => l.status === "active").length,
        ],
        [
          "Inactive",
          filteredLeaveTypes.filter((l) => l.status === "inactive").length,
        ],
        [
          "Paid Leave Types",
          filteredLeaveTypes.filter((l) => l.is_paid === 1).length,
        ],
        [
          "Unpaid Leave Types",
          filteredLeaveTypes.filter((l) => l.is_paid === 0).length,
        ],
        [
          "Requires Approval",
          filteredLeaveTypes.filter((l) => l.requires_approval === 1).length,
        ],
        [
          "Auto Approve",
          filteredLeaveTypes.filter((l) => l.requires_approval === 0).length,
        ],
      ],
      { origin: `A${row + 1}` },
    );
    row += 10;

    // Headers
    const headers = [
      "ID",
      "Code",
      "Name",
      "Color",
      "Type",
      "Requires Approval",
      "Max Days/Year",
      "Max Days/Month",
      "Carry Forward Limit",
      "Status",
      "Created At",
    ];
    XLSX.utils.sheet_add_aoa(ws, [headers], { origin: `A${row + 1}` });

    // Apply yellow header style to header row
    for (let i = 0; i < headers.length; i++) {
      const cellRef = XLSX.utils.encode_cell({ r: row, c: i });
      if (ws[cellRef]) {
        ws[cellRef].s = yellowHeaderStyle;
      }
    }
    row++;

    // Data
    filteredLeaveTypes.forEach((leaveType) => {
      XLSX.utils.sheet_add_aoa(
        ws,
        [
          [
            leaveType.id || "",
            leaveType.code || "",
            leaveType.name || "",
            leaveType.color || "#f59e0b",
            leaveType.is_paid === 1 ? "Paid" : "Unpaid",
            leaveType.requires_approval === 1 ? "Yes" : "No",
            leaveType.max_days_per_year || 0,
            leaveType.max_days_per_month || 0,
            leaveType.carry_forward_limit || 0,
            leaveType.status || "",
            leaveType.created_at ? formatDateTime(leaveType.created_at) : "",
          ],
        ],
        { origin: `A${row + 1}` },
      );
      row++;
    });

    // Column widths
    ws["!cols"] = headers.map((_, index) => {
      const widths = {
        0: 15, // ID
        1: 18, // Code
        2: 25, // Name
        3: 18, // Color
        4: 18, // Type
        5: 22, // Requires Approval
        6: 20, // Max Days/Year
        7: 20, // Max Days/Month
        8: 22, // Carry Forward Limit
        9: 18, // Status
        10: 25, // Created At
      };
      return { wch: widths[index] || 20 };
    });

    XLSX.utils.book_append_sheet(wb, ws, "Leave Types");

    // Write file
    const wbout = XLSX.write(wb, { bookType: "xlsx", type: "array" });
    const blob = new Blob([wbout], { type: "application/octet-stream" });
    saveAs(
      blob,
      `Leave_Types_Report_${new Date().toISOString().split("T")[0]}.xlsx`,
    );
  };

  const StatusBadge = ({ status }) => {
    if (status === "active") {
      return (
        <Chip
          icon={<CheckCircleIcon sx={{ fontSize: 14 }} />}
          label="Active"
          size="small"
          sx={{
            justifyContent: 'flex-start',
            width: 90,
            px: 0.5,
            backgroundColor: "#d1fae5",
            color: "#0abe31",
            fontWeight: 500,
            fontSize: "0.75rem",
            height: "30px",
            ...(theme.palette.mode === "dark" && {
              backgroundColor: "rgba(41, 185, 16, 0.25)",
              color: "#6ee774",
            }),
          }}
        />
      );
    }
    return (
      <Chip
        icon={<CancelIcon sx={{ fontSize: 14 }} />}
        label="Inactive"
        size="small"
        sx={{
          justifyContent: 'flex-start',
          width: 90,
          px: 0.5,
          backgroundColor: "#fee2e2",
          color: "#991b1b",
          fontWeight: 500,
          fontSize: "0.75rem",
          height: "30px",
          ...(theme.palette.mode === "dark" && {
            backgroundColor: "rgba(239,68,68,0.25)",
            color: "#fca5a5",
          }),
        }}
      />
    );
  };

  return (
    <Box
      sx={{
        minHeight: "100vh",
        // background:
        //   theme.palette.mode === "dark"
        //     ? "linear-gradient(135deg, #0a0f1e 0%, #0f172a 50%, #1e1b2e 100%)"
        //     : "linear-gradient(135deg, #fffbeb 0%, #ffffff 50%, #fefce8 100%)",
        p: 3,
      }}
    >
      {/* Header Section */}
      <Box sx={{ mb: 4 }}>
        <Stack
          direction={{ xs: "column", sm: "row" }}
          spacing={2}
          alignItems={{ xs: "flex-start", sm: "center" }}
          justifyContent="space-between"
          sx={{ width: "100%" }}
        >
          <Stack
            direction="row"
            spacing={2}
            alignItems="center"
            sx={{ flex: 1 }}
          >
            <Box
              component="img"
              src={leaveTypesSvg}
              alt="Leave Types"
              sx={{
                width: { xs: 64, lg: 80 },
                height: { xs: 64, lg: 80 },
                objectFit: "contain",
              }}
            />
            <Box>
              <Typography
                variant="h4"
                component="h1"
                sx={{
                  fontWeight: "bold",
                  color: "text.primary",
                }}
              >
                Leave Types
              </Typography>
              <Typography
                variant="body2"
                sx={{
                  color: "text.secondary",
                  mt: 0.5,
                }}
              >
                Manage leave policies and types
              </Typography>
            </Box>
          </Stack>

          <Box sx={{ display: "flex", gap: 2, alignItems: "center" }}>
            {/* Export Excel Button */}
            <Button
              variant="contained"
              startIcon={<DownloadIcon />}
              onClick={exportLeaveTypesToExcel}
              sx={{
                background: "linear-gradient(135deg, #25a55f 0%, #1e8b4e 100%)",
                color: "#ffffff",
                borderRadius: "8px",
                px: 2.5,
                py: 0.5,
                fontSize: "0.8125rem",
                fontWeight: 500,
                textTransform: "none",
                minWidth: "auto",
                whiteSpace: "nowrap",
                boxShadow: "none",
                transition: "all 0.2s ease",
                height: "36px",
                "&:hover": {
                  background:
                    "linear-gradient(135deg, #1e8b4e 0%, #16723e 100%)",
                  boxShadow: "0 2px 8px rgba(37, 165, 95, 0.3)",
                  transform: "translateY(-1px)",
                },
                "&:active": {
                  transform: "translateY(0)",
                },
              }}
            >
              Export Excel
            </Button>

            {hasPermission("leave_type.create") && (
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={() => setShowCreateModal(true)}
                sx={{
                  background:
                    "linear-gradient(135deg, #f59e0b 0%, #d97706 100%)",
                  borderRadius: "10px",
                  px: 2.5,
                  py: 1,
                  fontSize: "0.875rem",
                  fontWeight: 500,
                  textTransform: "none",
                  minWidth: "auto",
                  whiteSpace: "nowrap",
                  boxShadow: "none",
                  transition: "all 0.2s ease",
                  "&:hover": {
                    background:
                      "linear-gradient(135deg, #d97706 0%, #b45309 100%)",
                    boxShadow: "0 2px 8px rgba(245, 158, 11, 0.3)",
                    transform: "translateY(-1px)",
                  },
                  "&:active": {
                    transform: "translateY(0)",
                  },
                }}
              >
                Add Leave Type
              </Button>
            )}
          </Box>
        </Stack>
      </Box>
      {/* Search and Filter Section */}
      <Stack
        direction={{ xs: "column", sm: "row" }}
        spacing={2}
        alignItems={{ sm: "center" }}
        justifyContent="space-between"
        sx={{ mb: 3 }}
      >
        <TextField
          placeholder="Search by name or code..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          variant="outlined"
          size="small"
          sx={{
            flex: 1,
            maxWidth: { sm: 400 },
            "& .MuiOutlinedInput-root": {
              borderRadius: "12px",
              bgcolor: "background.paper",
              "& fieldset": {
                borderColor:
                  theme.palette.mode === "dark" ? "#334155" : "#e5e7eb",
              },
              "&:hover fieldset": {
                borderColor: "#f59e0b",
              },
              "&.Mui-focused fieldset": {
                borderColor: "#f59e0b",
                borderWidth: "2px",
              },
            },
          }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon sx={{ color: "#9ca3af" }} />
              </InputAdornment>
            ),
          }}
        />
        <Stack direction="row" spacing={1}>
          {["all", "active", "inactive"].map((filter) => (
            <Button
              key={filter}
              variant={statusFilter === filter ? "contained" : "outlined"}
              onClick={() => setStatusFilter(filter)}
              sx={{
                textTransform: "capitalize",
                borderRadius: "8px",
                px: 2,
                py: 0.7,
                fontSize: "0.875rem",
                fontWeight: 500,
                ...(statusFilter === filter && {
                  background:
                    "linear-gradient(135deg, #f59e0b 0%, #d97706 100%)",
                  boxShadow: "0 4px 6px -1px rgba(245, 158, 11, 0.25)",
                  "&:hover": {
                    background:
                      "linear-gradient(135deg, #d97706 0%, #b45309 100%)",
                  },
                }),
                ...(statusFilter !== filter && {
                  borderColor:
                    theme.palette.mode === "dark" ? "#475569" : "#d1d5db",
                  color: theme.palette.mode === "dark" ? "#d1d5db" : "#6b7280",
                  "&:hover": {
                    borderColor: "#f59e0b",
                    backgroundColor:
                      theme.palette.mode === "dark"
                        ? "rgba(245,158,11,0.1)"
                        : "#fffbeb",
                  },
                }),
              }}
            >
              {filter.charAt(0).toUpperCase() + filter.slice(1)}
            </Button>
          ))}
        </Stack>
      </Stack>

      {/* Loading State */}
      {loading ? (
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            height: 400,
          }}
        >
          <Stack alignItems="center" spacing={2}>
            <CircularProgress
              size={48}
              sx={{
                color: "primary.main",
                "& .MuiCircularProgress-circle": {
                  strokeLinecap: "round",
                },
              }}
            />
            <Typography
              sx={{
                color: "text.secondary",
              }}
            >
              Loading leave types...
            </Typography>
          </Stack>
        </Box>
      ) : (
        <Paper
          elevation={0}
          sx={{
            borderRadius: "16px",
            overflow: "hidden",
            boxShadow:
              theme.palette.mode === "dark"
                ? "0 10px 25px -5px rgba(0,0,0,0.3)"
                : "0 10px 25px -5px rgba(0,0,0,0.05), 0 8px 10px -6px rgba(0,0,0,0.02)",
            border: `1px solid ${theme.palette.mode === "dark" ? "#1e293b" : "#f3f4f6"
              }`,
          }}
        >
          <TableContainer sx={{ overflowX: "auto" }}>
            <Table sx={{ minWidth: 800 }}>
              <TableHead
                sx={{
                  backgroundColor:
                    theme.palette.mode === "dark" ? "#1e293b" : "#f9fafb",
                }}
              >
                <TableRow>
                  <TableCell
                    align="center"
                    sx={{
                      fontWeight: 600,
                      fontSize: "0.75rem",
                      color: "text.secondary",
                    }}
                  >
                    Code
                  </TableCell>
                  <TableCell
                    align="center"
                    sx={{
                      fontWeight: 600,
                      fontSize: "0.75rem",
                      color: "text.secondary",
                    }}
                  >
                    Name & Color
                  </TableCell>
                  <TableCell
                    align="center"
                    sx={{
                      fontWeight: 600,
                      fontSize: "0.75rem",
                      color: "text.secondary",
                    }}
                  >
                    Type & Approval
                  </TableCell>
                  <TableCell
                    align="center"
                    sx={{
                      fontWeight: 600,
                      fontSize: "0.75rem",
                      color: "text.secondary",
                    }}
                  >
                    Max Days/Year
                  </TableCell>
                  <TableCell
                    align="center"
                    sx={{
                      fontWeight: 600,
                      fontSize: "0.75rem",
                      color: "text.secondary",
                    }}
                  >
                    Max Days/Month
                  </TableCell>
                  <TableCell
                    align="center"
                    sx={{
                      fontWeight: 600,
                      fontSize: "0.75rem",
                      color: "text.secondary",
                    }}
                  >
                    Carry Forward
                  </TableCell>
                  <TableCell
                    align="center"
                    sx={{
                      fontWeight: 600,
                      fontSize: "0.75rem",
                      color: "text.secondary",
                    }}
                  >
                    Status
                  </TableCell>

                  <TableCell
                    align="center"
                    sx={{
                      fontWeight: 600,
                      fontSize: "0.75rem",
                      color: "text.secondary",
                    }}
                  >
                    Created At
                  </TableCell>
                  {hasAnyPermission([
                    "leave_type.update",
                    "leave_type.delete",
                  ]) && (
                      <TableCell
                        sx={{
                          fontWeight: 600,
                          fontSize: "0.75rem",
                          color: "text.secondary",
                        }}
                      >
                        Actions
                      </TableCell>
                    )}
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredLeaveTypes.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={9} align="center" sx={{ py: 6 }}>
                      <StyleIcon
                        sx={{ fontSize: 48, color: "#d1d5db", mb: 1 }}
                      />
                      <Typography
                        sx={{
                          color: "text.secondary",
                        }}
                      >
                        No leave types found. Click "Add Leave Type" to get
                        started.
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  filteredLeaveTypes
                    .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                    .map((leaveType, index) => (
                      <TableRow
                        key={leaveType.id}
                        component={motion.tr}
                        initial={{ opacity: 0, x: -20 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ delay: index * 0.05 }}
                        sx={{
                          "&:hover": {
                            backgroundColor:
                              theme.palette.mode === "dark"
                                ? "rgba(30,41,59,0.5)"
                                : "#f9fafb",
                          },
                        }}
                      >
                        <TableCell>
                          <Stack
                            direction="row"
                            spacing={1}
                            alignItems="center"
                          >
                            <StyleIcon
                              sx={{ fontSize: 16, color: "primary.main" }}
                            />
                            <Typography
                              sx={{
                                fontFamily: "monospace",
                                fontSize: "0.875rem",
                                fontWeight: 500,
                                color: "text.primary",
                              }}
                            >
                              {leaveType.code}
                            </Typography>
                          </Stack>
                        </TableCell>
                        <TableCell>
                          <Stack
                            direction="row"
                            spacing={1.5}
                            alignItems="center"
                          >
                            <Box
                              sx={{
                                width: 40,
                                height: 40,
                                borderRadius: "12px",
                                backgroundColor: leaveType.color || "#f59e0b",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                              }}
                            >
                              <MdEventBusy size={20} color="#ffffff" />
                            </Box>
                            <Box>
                              <Typography
                                sx={{
                                  fontWeight: 500,
                                  color: "text.primary",
                                }}
                              >
                                {leaveType.name}
                              </Typography>
                              <Typography
                                sx={{
                                  fontSize: "0.75rem",
                                  color:
                                    theme.palette.mode === "dark"
                                      ? "#6b7280"
                                      : "#9ca3af",
                                }}
                              >
                                ID: {leaveType.id} | Tenant:{" "}
                                {leaveType.tenant_id}
                              </Typography>
                            </Box>
                          </Stack>
                        </TableCell>
                        <TableCell>
                          <Stack spacing={0.5}>
                            <Chip
                              label={
                                leaveType.is_paid === 1
                                  ? "Paid Leave"
                                  : "Unpaid Leave"
                              }
                              size="small"
                              sx={{
                                backgroundColor:
                                  leaveType.is_paid === 1
                                    ? "#d1fae5"
                                    : "#fee2e2",
                                color:
                                  leaveType.is_paid === 1
                                    ? "#065f46"
                                    : "#991b1b",
                                fontWeight: 500,
                                fontSize: "0.7rem",
                                height: "22px",
                                width: "fit-content",
                                ...(theme.palette.mode === "dark" && {
                                  backgroundColor:
                                    leaveType.is_paid === 1
                                      ? "rgba(16,185,129,0.25)"
                                      : "rgba(239,68,68,0.25)",
                                  color:
                                    leaveType.is_paid === 1
                                      ? "#6ee7b7"
                                      : "#fca5a5",
                                }),
                              }}
                            />
                            <Chip
                              label={
                                leaveType.requires_approval === 1
                                  ? "Approval Required"
                                  : "Auto Approve"
                              }
                              size="small"
                              sx={{
                                backgroundColor:
                                  leaveType.requires_approval === 1
                                    ? "#fef3c7"
                                    : "#dbeafe",
                                color:
                                  leaveType.requires_approval === 1
                                    ? "#92400e"
                                    : "#1e40af",
                                fontWeight: 500,
                                fontSize: "0.7rem",
                                height: "22px",
                                width: "fit-content",
                                ...(theme.palette.mode === "dark" && {
                                  backgroundColor:
                                    leaveType.requires_approval === 1
                                      ? "rgba(245,158,11,0.25)"
                                      : "rgba(59,130,246,0.25)",
                                  color:
                                    leaveType.requires_approval === 1
                                      ? "#fde68a"
                                      : "#93c5fd",
                                }),
                              }}
                            />
                          </Stack>
                        </TableCell>
                        <TableCell>
                          <Typography
                            sx={{
                              fontSize: "0.875rem",
                              fontWeight: 600,
                              color: "text.primary",
                            }}
                          >
                            {leaveType.max_days_per_year} days
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography
                            sx={{
                              fontSize: "0.875rem",
                              fontWeight: 600,
                              color: "text.primary",
                            }}
                          >
                            {leaveType.max_days_per_month || 0} days
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography
                            sx={{
                              fontSize: "0.875rem",
                              color:
                                theme.palette.mode === "dark"
                                  ? "#d1d5db"
                                  : "#4b5563",
                            }}
                          >
                            {leaveType.carry_forward_limit || 0} days
                          </Typography>
                        </TableCell>
                        <TableCell align="center">
                          {hasPermission("leave_type.update") ? (
                            <Box
                              onClick={() => handleStatusToggle(leaveType)}
                              sx={{
                                display: "inline-flex",
                                backgroundColor: "transparent",
                                border: `1px solid ${theme.palette.mode === "dark"
                                  ? "#475569"
                                  : "#d1d5db"
                                  }`,
                                borderRadius: "40px",
                                padding: "2px",
                                cursor: "pointer",
                                width: "fit-content",
                                minWidth: "140px",
                              }}
                            >
                              <Box
                                sx={{
                                  flex: 1,
                                  px: 2,
                                  py: 0.5,
                                  borderRadius: "40px",
                                  backgroundColor:
                                    leaveType.status === "active"
                                      ? theme.palette.mode === "dark"
                                        ? "#10b981"
                                        : "#d1fae5"
                                      : "transparent",
                                  color:
                                    leaveType.status === "active"
                                      ? theme.palette.mode === "dark"
                                        ? "#ffffff"
                                        : "#065f46"
                                      : theme.palette.mode === "dark"
                                        ? "#9ca3af"
                                        : "#6b7280",
                                  fontWeight: 500,
                                  fontSize: "0.75rem",
                                  textAlign: "center",
                                  transition: "all 0.2s ease",
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "center",
                                  gap: 0.5,
                                }}
                              >
                                <CheckCircleIcon sx={{ fontSize: 14 }} />
                                Active
                              </Box>
                              <Box
                                sx={{
                                  flex: 1,
                                  px: 2,
                                  py: 0.5,
                                  borderRadius: "40px",
                                  backgroundColor:
                                    leaveType.status === "inactive"
                                      ? theme.palette.mode === "dark"
                                        ? "#ef4444"
                                        : "#fee2e2"
                                      : "transparent",
                                  color:
                                    leaveType.status === "inactive"
                                      ? theme.palette.mode === "dark"
                                        ? "#ffffff"
                                        : "#991b1b"
                                      : theme.palette.mode === "dark"
                                        ? "#9ca3af"
                                        : "#6b7280",
                                  fontWeight: 500,
                                  fontSize: "0.75rem",
                                  textAlign: "center",
                                  transition: "all 0.2s ease",
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "center",
                                  gap: 0.5,
                                }}
                              >
                                <CancelIcon sx={{ fontSize: 14 }} />
                                Inactive
                              </Box>
                            </Box>
                          ) : (
                            <StatusBadge status={leaveType.status} />
                          )}
                        </TableCell>
                        <TableCell>
                          <Stack spacing={0.5}>
                            <Stack
                              direction="row"
                              spacing={1}
                              alignItems="center"
                            >
                              <EventIcon
                                sx={{ fontSize: 14, color: "primary.main" }}
                              />
                              <Typography
                                sx={{
                                  fontSize: "0.875rem",
                                  color:
                                    theme.palette.mode === "dark"
                                      ? "#d1d5db"
                                      : "#4b5563",
                                }}
                              >
                                {formatDateTime(leaveType.created_at)}
                              </Typography>
                            </Stack>
                            {leaveType.updated_at &&
                              leaveType.updated_at !== leaveType.created_at && (
                                <Stack
                                  direction="row"
                                  spacing={1}
                                  alignItems="center"
                                  sx={{ opacity: 0.6 }}
                                >
                                  <Typography
                                    sx={{
                                      fontSize: "0.7rem",
                                      color: "text.secondary",
                                      ml: 3.5,
                                    }}
                                  >
                                    <b>Updated: </b>{" "}
                                    {formatDateTime(leaveType.updated_at)}
                                  </Typography>
                                </Stack>
                              )}
                          </Stack>
                        </TableCell>
                        <TableCell align="center">
                          <Stack
                            direction="row"
                            spacing={1}
                            justifyContent="center"
                          >
                            {hasPermission("leave_type.update") && (
                              <IconButton
                                onClick={() => {
                                  setSelectedLeaveType(leaveType);
                                  setShowEditModal(true);
                                }}
                                size="small"
                                sx={{
                                  color: "primary.main",
                                  "&:hover": {
                                    backgroundColor:
                                      theme.palette.mode === "dark"
                                        ? "rgba(245,158,11,0.2)"
                                        : "#f6e5a1",
                                  },
                                }}
                              >
                                <EditIcon />
                              </IconButton>
                            )}
                            {hasPermission("leave_type.delete") && (
                              <IconButton
                                onClick={() => handleDeleteClick(leaveType)}
                                size="small"
                                sx={{
                                  color: "#ef4444",
                                  "&:hover": {
                                    backgroundColor:
                                      theme.palette.mode === "dark"
                                        ? "rgba(239,68,68,0.2)"
                                        : "#fcc9c9be",
                                  },
                                }}
                              >
                                <DeleteIcon />
                              </IconButton>
                            )}
                          </Stack>
                        </TableCell>
                      </TableRow>
                    ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      )}

      {/* Pagination */}
      {!loading && filteredLeaveTypes.length > 0 && (
        <TablePagination
          rowsPerPageOptions={[5, 10, 25, 50]}
          component="div"
          count={filteredLeaveTypes.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          sx={{
            mt: 2,
            color: "text.secondary",
            ".MuiTablePagination-selectIcon, .MuiTablePagination-actions button":
            {
              color: "text.secondary",
            },
          }}
        />
      )}

      {/* Empty State for No Search Results */}
      {!loading && filteredLeaveTypes.length === 0 && leaveTypes.length > 0 && (
        <Paper
          sx={{
            mt: 4,
            p: 4,
            textAlign: "center",
            borderRadius: "16px",
            bgcolor: "background.paper",
          }}
        >
          <SearchIcon sx={{ fontSize: 48, color: "#d1d5db", mb: 1 }} />
          <Typography
            sx={{
              color: "text.secondary",
              mb: 2,
            }}
          >
            No leave types match your search criteria
          </Typography>
          <Button
            onClick={() => {
              setSearchTerm("");
              setStatusFilter("all");
              setPage(0);
            }}
            sx={{
              color: "primary.main",
              textTransform: "none",
              "&:hover": {
                backgroundColor:
                  theme.palette.mode === "dark"
                    ? "rgba(245,158,11,0.1)"
                    : "#fffbeb",
              },
            }}
          >
            Clear filters
          </Button>
        </Paper>
      )}

      {/* Modals */}
      <AddLeaveTypeModal
        isOpen={showCreateModal}
        onClose={() => setShowCreateModal(false)}
        onSuccess={() => dispatch(fetchLeaveTypes())}
      />

      <EditLeaveTypeModal
        isOpen={showEditModal}
        onClose={() => {
          setShowEditModal(false);
          setSelectedLeaveType(null);
        }}
        leaveType={selectedLeaveType}
        onSuccess={() => dispatch(fetchLeaveTypes())}
      />
      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={handleDeleteCancel}
        maxWidth="xs"
        fullWidth
        TransitionComponent={motion.div}
        TransitionProps={{
          timeout: 300,
          style: { overflow: "visible" },
        }}
        sx={{
          "& .MuiDialog-paper": {
            borderRadius: "20px",
            overflow: "hidden",
            backgroundColor:
              theme.palette.mode === "dark" ? "#0f172a" : "#ffffff",
            maxWidth: "400px",
            margin: "16px",
            boxShadow: theme.shadows[24],
            position: "relative",
          },
        }}
        slotProps={{
          backdrop: {
            sx: {
              backgroundColor: "rgba(0, 0, 0, 0.6)",
              backdropFilter: "blur(4px)",
            },
          },
        }}
      >
        <motion.div
          initial={{ x: "100%", opacity: 0 }}
          animate={{ x: 0, opacity: 1 }}
          exit={{ x: "100%", opacity: 0 }}
          transition={{ type: "spring", damping: 25, stiffness: 200 }}
        >
          {/* Yellow Banner */}
          <Box
            sx={{
              background: "linear-gradient(135deg, #f59e0b 0%, #d97706 100%)",
              height: "6px",
            }}
          />

          <Box sx={{ p: 3, position: "relative" }}>
            {/* Close Icon Button */}
            <IconButton
              onClick={handleDeleteCancel}
              sx={{
                position: "absolute",
                top: 16,
                right: 16,
                width: 32,
                height: 32,
                borderRadius: "8px",
                color: "text.secondary",
                "&:hover": {
                  backgroundColor: "#f59e0b",
                  color: "#ffffff",
                },
              }}
            >
              <CloseIcon sx={{ fontSize: 18 }} />
            </IconButton>

            {/* SVG Icon */}
            <Box
              component="img"
              src="/src/assets/modals/delete-modal.svg"
              alt="Inactivate"
              sx={{
                width: 80,
                height: 80,
                objectFit: "contain",
                margin: "0 auto",
                display: "block",
                mb: 2,
              }}
              onError={(e) => {
                e.target.style.display = "none";
              }}
            />

            <Typography
              sx={{
                fontWeight: 700,
                fontSize: "1.25rem",
                color: "text.primary",
                textAlign: "center",
                mb: 2,
              }}
            >
              Inactivate Leave Type
            </Typography>

            <Typography
              sx={{
                color: theme.palette.mode === "dark" ? "#d1d5db" : "#4b5563",
                fontSize: "0.95rem",
                lineHeight: 1.5,
                textAlign: "center",
                mb: 1,
              }}
            >
              Are you sure you want to inactivate
            </Typography>

            <Typography
              sx={{
                fontWeight: 600,
                color: "primary.main",
                fontSize: "1rem",
                textAlign: "center",
                mb: 2,
                wordBreak: "break-word",
              }}
            >
              "{leaveTypeToDelete?.name}"
            </Typography>

            <Typography
              sx={{
                color: "text.secondary",
                fontSize: "0.85rem",
                lineHeight: 1.4,
                textAlign: "center",
              }}
            >
              This leave type will become inactive and won't be available for future leave requests. This will be permanently deleted after 256 days.
            </Typography>
          </Box>

          <Box sx={{ p: 3, pt: 0, display: "flex", gap: 2 }}>
            <Button
              onClick={handleDeleteCancel}
              disabled={deleting}
              fullWidth
              sx={{
                textTransform: "none",
                fontWeight: 600,
                borderRadius: "12px",
                py: 1,
                fontSize: "0.875rem",
                color: theme.palette.mode === "dark" ? "#d1d5db" : "#4b5563",
                backgroundColor:
                  theme.palette.mode === "dark" ? "#1e293b" : "#f3f4f6",
                border: `1px solid ${theme.palette.mode === "dark" ? "#334155" : "#e5e7eb"
                  }`,
                "&:hover": {
                  backgroundColor:
                    theme.palette.mode === "dark" ? "#334155" : "#e5e7eb",
                },
              }}
            >
              Cancel
            </Button>
            <Button
              onClick={handleDeleteConfirm}
              disabled={deleting}
              fullWidth
              variant="contained"
              sx={{
                textTransform: "none",
                fontWeight: 600,
                borderRadius: "12px",
                py: 1,
                fontSize: "0.875rem",
                background: "linear-gradient(135deg, #f59e0b 0%, #d97706 100%)",
                color: "#ffffff",
                boxShadow: "0 4px 12px rgba(245, 158, 11, 0.3)",
                "&:hover": {
                  background:
                    "linear-gradient(135deg, #d97706 0%, #b45309 100%)",
                  boxShadow: "0 6px 16px rgba(245, 158, 11, 0.4)",
                  transform: "translateY(-1px)",
                },
                "&:active": {
                  transform: "translateY(0)",
                },
                "&:disabled": {
                  background:
                    "linear-gradient(135deg, #f59e0b 0%, #d97706 100%)",
                  opacity: 0.6,
                },
              }}
            >
              {deleting ? (
                <CircularProgress size={20} sx={{ color: "#ffffff" }} />
              ) : (
                "Inactivate"
              )}
            </Button>
          </Box>
        </motion.div>
      </Dialog>
    </Box>
  );
};

export default LeaveType;
